// NEW FILE — org-wide "WhatsApp Workflow History" page.
// Mirrors pages/pushnotifications/PushNotificationHistory.js exactly in
// style, but reads from the WhatsApp Workflow execution log instead of the
// push-notification log, and shows a per-recipient breakdown on expand.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getWhatsAppWorkflowHistory } from "../../services/WhatsappWorkflowApi";

const statusBadge = (status) => {
  const styles = {
    success: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
    running: "bg-yellow-100 text-yellow-700",
  };
  return styles[status] || "bg-gray-100 text-gray-500";
};

const WhatsAppWorkflowHistory = () => {
  const [loading, setLoading] = useState(true);
  const [executions, setExecutions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await getWhatsAppWorkflowHistory();
      setExecutions(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load WhatsApp workflow history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const recipientCount = (ex) => {
    const step = ex.steps?.find((s) => s.nodeType === "whatsapp");
    return Array.isArray(step?.output) ? step.output.length : step?.output ? 1 : 0;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">WhatsApp Workflow History</h2>

        <button onClick={fetchHistory} className="px-4 py-2 rounded bg-green-600 text-white">
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left">Workflow</th>
              <th className="px-4 py-3 text-left">Target Type</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Recipients</th>
              <th className="px-4 py-3 text-left">Duration</th>
              <th className="px-4 py-3 text-left">Date</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-8">
                  Loading...
                </td>
              </tr>
            ) : executions.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8">
                  No WhatsApp Workflow History
                </td>
              </tr>
            ) : (
              executions.map((ex) => (
                <React.Fragment key={ex._id}>
                  <tr
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === ex._id ? null : ex._id)}
                  >
                    <td className="px-4 py-3">{ex.workflow?.name || "(deleted workflow)"}</td>
                    <td className="px-4 py-3 capitalize">{ex.workflow?.targetType || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${statusBadge(ex.status)}`}>
                        {ex.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{recipientCount(ex)}</td>
                    <td className="px-4 py-3">{ex.durationMs || 0}ms</td>
                    <td className="px-4 py-3">{new Date(ex.createdAt).toLocaleString()}</td>
                  </tr>

                  {expandedId === ex._id && (
                    <tr className="bg-gray-50">
                      <td colSpan="6" className="px-4 py-3">
                        {ex.error && (
                          <p className="text-sm text-red-600 mb-2">Error: {ex.error}</p>
                        )}
                        <div className="text-xs space-y-1">
                          {(ex.steps || []).map((s, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full ${statusBadge(s.status)}`}>
                                {s.nodeType}
                              </span>
                              <span className="text-gray-500">{s.durationMs || 0}ms</span>
                              {s.error && <span className="text-red-500">— {s.error}</span>}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WhatsAppWorkflowHistory;
